import { cn } from '@/lib';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MaskedValueProps {
  // The secret to display. undefined renders the "not set" state instead of a masked value.
  value?: string;
  // 'cells' renders one box per character (short PINs); 'inline' renders one monospace field.
  variant?: 'cells' | 'inline';
  // Seconds after which a revealed value hides itself again. 0 disables the auto-hide.
  autoHideSeconds?: number;
  // Hint text shown under the value while masked / revealed without a countdown.
  revealHint?: string;
  hideHint?: string;
  notSetLabel?: string;
  className?: string;
}

const MASK_CHARACTER = '•';
const DEFAULT_AUTO_HIDE_SECONDS = 10;

/**
 * @component MaskedValue
 * @description Displays a secret (PIN, key) masked by default. The whole block is a button:
 *              clicking it reveals the value, and clicking again (or the auto-hide countdown)
 *              masks it again. Shows a neutral "not set" state when no value exists. Holds the
 *              value only in props; nothing is persisted.
 *
 * @props
 *   value (string, optional) - Secret to display. Omit for the "not set" state.
 *   variant ('cells' | 'inline', optional, default: 'cells') - Per-character boxes or one inline field.
 *   autoHideSeconds (number, optional, default: 10) - Countdown before auto re-masking; 0 disables.
 *   revealHint / hideHint / notSetLabel (string, optional) - Override the translated hint/state text.
 *   className (string, optional) - Extra classes for the wrapper.
 *
 * @notes
 *   Never logs or stores the value. The revealed state resets whenever the value changes.
 *
 * @changelog
 *   2026-09-07 - Initial creation for the device rules PINs and user level keys.
 *   2026-09-07 - Reworked into a single clickable block with a hint/countdown line, no separate button.
 */
export function MaskedValue({
  value,
  variant = 'cells',
  autoHideSeconds = DEFAULT_AUTO_HIDE_SECONDS,
  revealHint,
  hideHint,
  notSetLabel,
  className,
}: MaskedValueProps) {
  const { t } = useTranslation();
  const { isRevealed, secondsLeft, toggle } = useRevealCountdown(value, autoHideSeconds);

  if (value === undefined) {
    return (
      <span className={cn('text-muted text-sm italic', className)}>
        {notSetLabel ?? t('common.notSet')}
      </span>
    );
  }

  const displayedValue = isRevealed ? value : MASK_CHARACTER.repeat(value.length);
  const hasCountdown = isRevealed && autoHideSeconds > 0;
  const hintText = isRevealed
    ? hasCountdown
      ? t('common.masked.hidesIn', { count: secondsLeft })
      : hideHint ?? t('common.masked.clickToHide')
    : revealHint ?? t('common.masked.clickToReveal');

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={isRevealed}
      className={cn('flex flex-col items-start gap-1 text-left', className)}
    >
      <MaskedValueDisplay value={displayedValue} isRevealed={isRevealed} variant={variant} />
      <span className="text-muted text-xs">{hintText}</span>
    </button>
  );
}

interface MaskedValueDisplayProps {
  value: string;
  isRevealed: boolean;
  variant: 'cells' | 'inline';
}

// MaskedValueDisplay renders the (masked or plain) characters in the chosen visual variant.
function MaskedValueDisplay({ value, isRevealed, variant }: MaskedValueDisplayProps) {
  const textColor = isRevealed ? 'text-onfield' : 'text-muted';

  if (variant === 'inline') {
    return (
      <span
        className={cn(
          'bg-field border-border inline-block min-w-46 rounded-lg border px-3.5 py-2 font-mono text-lg tracking-widest',
          textColor
        )}
      >
        {value}
      </span>
    );
  }

  // One box per character
  return (
    <div className="flex gap-2">
      {value.split('').map((character, index) => (
        <span
          key={index}
          className={cn(
            'bg-field border-border flex h-13 w-11 items-center justify-center rounded-lg border font-mono text-2xl',
            textColor
          )}
        >
          {character}
        </span>
      ))}
    </div>
  );
}

// useRevealCountdown tracks which value is revealed and counts down to an automatic re-mask.
// Because the revealed value is compared with the current one, a changed secret is masked
// again without any reset logic.
function useRevealCountdown(value: string | undefined, autoHideSeconds: number) {
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const hasCountdown = autoHideSeconds > 0;
  const isRevealed = revealedValue !== null && revealedValue === value && (!hasCountdown || secondsLeft > 0);

  useEffect(() => {
    const shouldCountDown = isRevealed && hasCountdown;
    if (!shouldCountDown) return;

    const timer = setInterval(() => setSecondsLeft((current) => Math.max(current - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [isRevealed, hasCountdown]);

  const toggle = () => {
    setRevealedValue(isRevealed ? null : value ?? null);
    setSecondsLeft(autoHideSeconds);
  };

  return { isRevealed, secondsLeft, toggle };
}
